import React, { useEffect, useState } from "react";
import Link from "next/link";
import PropTypes from "prop-types";
import { Button, Spinner, TextInput } from "@aqar-app/aqar-design-system";
import { useApolloClient, useMutation } from "@apollo/client";
import styles from "./auth.module.scss";
import { getDeviceID, isSaudiNumber } from "../../lib/helpers";
import useForm from "../../hooks/useForm";
import FieldError from "../../components/field-error/FieldError";
import { LOGIN, OTP_LOGIN } from "../../graphql/mutations/auth";
import { setCookie, setLoginCookies } from "../../lib/cookiesHelpers";
import { trackLoginEvent } from "../../lib/tracking/trackAuthenticationEvents";
import { useAuthState } from "../../contexts/auth/auth.provider";
import { getJwt } from "@/lib/paymentHelpers";
import { log } from "@/console";
import { USER_JWT_EXPIRATION } from "@/lib/serverOnly";

const loginFieldsValidator = (values) => {
  const errors = {};
  if (!values.phone || !isSaudiNumber(values.phone)) {
    errors.phone = "مطلوب رقم جوال سعودي";
  }
  if (!values.password) {
    errors.password = "مطلوب كلمة المرور";
  }

  return errors;
};

const SignInForm = ({ otp, redirectPostLogin }) => {
  const apolloClient = useApolloClient();
  const [mutationError, setMutationError] = useState(null);
  const { updateContext } = useAuthState();
  const [loginMutation, { loading }] = useMutation(LOGIN, {
    // eslint-disable-next-line no-use-before-define
    onError: handleLoginError,
    onCompleted: ({ User }) => {
      const { token, user } = User.login;
      trackLoginEvent({ user });
      apolloClient.resetStore();
      // eslint-disable-next-line no-use-before-define
      handleLoginSuccess(token, user);
    },
  });

  const [otpLoginMutation, { error: otpLoginError }] = useMutation(OTP_LOGIN, {
    variables: { pw: otp },
    // eslint-disable-next-line no-use-before-define
    onError: handleLoginError,
    onCompleted: ({ User }) => {
      const { token, user } = User.loginOTP;
      trackLoginEvent({ user });
      apolloClient.resetStore();
      // eslint-disable-next-line no-use-before-define
      handleLoginSuccess(token, user);
    },
  });

  const { values, errors, handleChange, handleSubmit } = useForm(
    // eslint-disable-next-line no-use-before-define
    login,
    loginFieldsValidator,
  );

  function handleLoginSuccess(token, user) {
    setLoginCookies(token, user);
    updateContext({ token, user });
    getJwt({ ...user, about: undefined }, USER_JWT_EXPIRATION)
      .then((res) => {
        setCookie("user", res.token, Date.now() + 1000 * 60 * 15);
      })
      .catch(log);
    redirectPostLogin();
  }

  function handleLoginError(err) {
    if (err.message === "كلمة المرور غير صحيحة") {
      return setMutationError({ password: "كلمة المرور غير صحيحة" });
    }

    if (err.message === "المستخدم غير موجود") {
      return setMutationError({ phone: "المستخدم غير موجود" });
    }

    return setMutationError({ general: err.message });
  }

  function getLoginMutationVariables() {
    const { phone, password } = values;
    return { phone, pw: password, device_id: getDeviceID() };
  }

  function login() {
    const variables = getLoginMutationVariables();
    return loginMutation({ variables });
  }

  const isFieldInvalid = (field) => {
    return !!errors[field] || (mutationError || {})[field];
  };

  const getFieldError = (field) => {
    return errors[field] || mutationError[field];
  };

  useEffect(() => {
    if (otp) {
      otpLoginMutation();
    }
  }, [otpLoginMutation, otp]);

  if (otp && !otpLoginError) {
    return (
      <div className={styles.spinnerWrapper}>
        <Spinner className={styles.spinner3rem} />
      </div>
    );
  }

  return (
    <>
      <div className={styles.label}>رقم الجوال</div>
      <div>
        <TextInput
          invalid={isFieldInvalid("phone")}
          name="phone"
          placeholder="الرجاء كتابة رقم الجوال..."
          value={values.phone || ""}
          type="number"
          onChange={handleChange}
          onPressEnter={handleSubmit}
        />
        {isFieldInvalid("phone") && (
          <FieldError text={getFieldError("phone")} />
        )}
      </div>
      <div className={styles.label}>كلمة المرور</div>
      <div>
        <TextInput.PasswordInput
          name="password"
          placeholder="الرجاء كتابة كلمة المرور..."
          invalid={isFieldInvalid("password")}
          value={values.password || ""}
          onChange={handleChange}
          onPressEnter={handleSubmit}
        />
        {isFieldInvalid("password") && (
          <FieldError text={getFieldError("password")} />
        )}
      </div>
      <div className={styles.generalError}>
        {isFieldInvalid("general") && (
          <FieldError text={getFieldError("general")} />
        )}
      </div>
      <Button
        className={styles.actionButton}
        contentClassName={styles.content}
        label="دخول"
        priority="primary"
        onClick={handleSubmit}
        disabled={loading}
      />
      <div className={styles.link}>
        <Link href="/forgot-password">هل نسيت كلمة المرور؟</Link>
      </div>
    </>
  );
};

SignInForm.propTypes = {
  redirectPostLogin: PropTypes.func.isRequired,
  otp: PropTypes.string,
};

SignInForm.defaultProps = {
  otp: "",
};
export default SignInForm;
